import React, { useMemo } from 'react';
import { TourProvider, components as TourComponents } from '@reactour/tour';
import {
  createLywOnboardingTourSteps,
  lywOnboardingTourStyles,
  navigateToDashboardAfterTour,
  useLywTourStorageKey,
} from './lywOnboardingTourSteps';
import { TourPopoverPointerOverlay } from './TourPopoverPointerOverlay';
import { LywTourTwoRowNavigation } from './LywTourTwoRowNavigation';

interface TourProviderWrapperProps {
  children: React.ReactNode;
}

/**
 * Wraps the app with TourProvider at the root level so the tour never unmounts
 * when navigating. Uses a portal internally, so location/route changes don't affect it.
 */
export function TourProviderWrapper({ children }: TourProviderWrapperProps) {
  const steps = useMemo(() => createLywOnboardingTourSteps(), []);
  const tourStorageKey = useLywTourStorageKey();

  const handleClose = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(tourStorageKey, 'true');
      navigateToDashboardAfterTour();
    }
  };

  const handleClickClose = ({ setIsOpen }: { setIsOpen: (open: boolean) => void }) => {
    handleClose();
    setIsOpen(false);
  };

  return (
    <TourProvider
        steps={steps}
        styles={lywOnboardingTourStyles}
        disableWhenSelectorFalsy={false}
        showBadge
        showNavigation
        showCloseButton
        showPrevNextButtons
        scrollSmooth
        beforeClose={handleClose}
        onClickClose={handleClickClose}
        components={{
          Navigation: LywTourTwoRowNavigation,
          Close: ({ onClick }) => (
            <button
              type="button"
              onClick={onClick}
              className="absolute right-4 top-4 cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Skip tour
            </button>
          ),
        }}
        prevButton={({ Button, currentStep, setCurrentStep }) => (
          <Button
            kind="prev"
            hideArrow
            onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
          >
            <span className="inline-flex min-h-11 min-w-11 items-center justify-center gap-2 px-3 py-2">
              <TourComponents.Arrow
                styles={lywOnboardingTourStyles}
                inverted={false}
                disabled={currentStep === 0}
              />
            </span>
          </Button>
        )}
        nextButton={({ Button, stepsLength, currentStep, setCurrentStep, setIsOpen }) => {
          const isLast = currentStep === stepsLength - 1;
          return (
            <Button
              hideArrow
              onClick={() => {
                if (isLast) {
                  handleClose();
                  setIsOpen(false);
                } else {
                  setCurrentStep((s) => s + 1);
                }
              }}
            >
              <span className="inline-flex min-h-11 min-w-[6.5rem] items-center justify-center gap-2 px-3 py-2">
                {!isLast && (
                  <TourComponents.Arrow
                    styles={lywOnboardingTourStyles}
                    inverted
                    disabled={false}
                  />
                )}
                <span className="text-sm font-medium">{isLast ? 'Finish' : 'Next'}</span>
              </span>
            </Button>
          );
        }}
      >
        <>
          {children}
          <TourPopoverPointerOverlay />
        </>
      </TourProvider>
  );
}
